import React from 'react';
import { Button } from 'primereact/button';
import { useMasking } from '../layout/context/maskingcontext';

/**
 * ProtectedContent Component
 *
 * Wraps sensitive content (documents, reports) and shows a placeholder
 * when data is masked. User must enter PIN to view the content.
 *
 * @param {React.ReactNode} children - The protected content to display when unmasked
 * @param {string} type - Type of content: 'document', 'report', or 'generic'
 * @param {string} title - Optional title to display in placeholder (e.g., "PAN Card", "Equifax Report")
 * @param {string} className - Optional additional CSS classes
 * @param {object} style - Optional inline styles
 * @param {boolean} showCount - If true, shows count indicator (e.g., "3 Documents")
 * @param {number} count - Number of items (used with showCount)
 */
const ProtectedContent = ({
    children,
    type = 'generic',
    title = '',
    className = '',
    style = {},
    showCount = false,
    count = 0
}) => {
    const { isMasked, requestUnlock, hasPin } = useMasking();

    // If not masked, show the actual content
    if (!isMasked) {
        return children;
    }

    // Determine icon and message based on type
    const getConfig = () => {
        switch (type) {
            case 'document':
                return {
                    icon: 'pi-file',
                    message: title ? `${title} is protected` : 'Document is protected',
                    buttonLabel: 'Enter PIN to View Document'
                };
            case 'report':
                return {
                    icon: 'pi-chart-bar',
                    message: title ? `${title} is protected` : 'Report is protected',
                    buttonLabel: 'Enter PIN to View Report'
                };
            case 'documents-gallery':
                return {
                    icon: 'pi-images',
                    message: showCount && count > 0
                        ? `${count} Document${count > 1 ? 's' : ''} available`
                        : 'Documents are protected',
                    buttonLabel: 'Enter PIN to View Documents'
                };
            case 'reports-list':
                return {
                    icon: 'pi-file-pdf',
                    message: showCount && count > 0
                        ? `${count} Report${count > 1 ? 's' : ''} available`
                        : 'Reports are protected',
                    buttonLabel: 'Enter PIN to View Reports'
                };
            default:
                return {
                    icon: 'pi-lock',
                    message: title || 'Content is protected',
                    buttonLabel: 'Enter PIN to View'
                };
        }
    };

    const config = getConfig();

    return (
        <div
            className={`protected-content-placeholder ${className}`}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3rem 2rem',
                backgroundColor: 'var(--surface-50)',
                border: '2px dashed var(--surface-300)',
                borderRadius: '12px',
                minHeight: '200px',
                ...style
            }}
        >
            {/* Lock Icon with Animation */}
            <div
                style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--surface-100)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}
            >
                <i
                    className={`pi ${config.icon}`}
                    style={{
                        fontSize: '2.5rem',
                        color: 'var(--primary-color)',
                        opacity: 0.7
                    }}
                />
            </div>

            {/* Protection Message */}
            <div
                style={{
                    textAlign: 'center',
                    marginBottom: '1.5rem'
                }}
            >
                <h4
                    style={{
                        margin: '0 0 0.5rem 0',
                        color: 'var(--text-color)',
                        fontWeight: '600'
                    }}
                >
                    <i className="pi pi-lock mr-2" style={{ fontSize: '1rem' }} />
                    {config.message}
                </h4>
                <p
                    style={{
                        margin: 0,
                        color: 'var(--text-color-secondary)',
                        fontSize: '0.9rem'
                    }}
                >
                    Enter your security PIN to access this content
                </p>
            </div>

            {/* Unlock Button */}
            <Button
                label={hasPin === false ? 'Set PIN to View' : config.buttonLabel}
                icon={hasPin === false ? 'pi pi-key' : 'pi pi-unlock'}
                onClick={requestUnlock}
                className="p-button-rounded"
                style={{
                    padding: '0.75rem 1.5rem',
                    fontSize: '0.95rem'
                }}
            />

            {/* Additional Info */}
            <p
                style={{
                    marginTop: '1rem',
                    color: 'var(--text-color-secondary)',
                    fontSize: '0.8rem',
                    opacity: 0.7
                }}
            >
                <i className="pi pi-shield mr-1" />
                Protected for your security
            </p>
        </div>
    );
};

export default ProtectedContent;
